import React, { useEffect, useState } from "react";
import { FileBarChart, Download, FileSpreadsheet, FileText } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError, arr, downloadFile } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/common/StatCard";
import ChartCard, { chartTooltipStyle } from "@/components/common/ChartCard";
import { CardSkeletonGrid } from "@/components/common/Loading";

export default function ClientReports() {
  const [report, setReport] = useState(null);
  useEffect(() => { api.get("/reports/monthly").then(({ data }) => setReport(data)).catch(() => {}); }, []);

  const dl = async (type, ext) => {
    try { await downloadFile(`/reports/export-${type}`, {}, `report.${ext}`); toast.success(`${type.toUpperCase()} downloaded`); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!report) return <div className="space-y-6"><PageHeader title="Reports" /><CardSkeletonGrid /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" subtitle="Your monthly performance summary" icon={FileBarChart}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => dl("pdf", "pdf")} data-testid="report-pdf-btn"><FileText className="mr-1 h-4 w-4" /> PDF</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => dl("excel", "xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => dl("csv", "csv")}><Download className="mr-1 h-4 w-4" /> CSV</Button>
          </div>
        } />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Rating" value={report.summary.rating} accent="amber" />
        <StatCard label="Total Reviews" value={report.summary.totalReviews} accent="brand" />
        <StatCard label="New Reviews" value={report.summary.newReviews} accent="green" />
        <StatCard label="Replies Sent" value={report.summary.repliesSent} accent="blue" />
        <StatCard label="Posts Published" value={report.summary.postsPublished} accent="purple" />
        <StatCard label="Requests Sent" value={report.summary.requestsSent} accent="brand" />
        <StatCard label="Negative Handled" value={report.summary.negativeHandled} accent="red" />
      </div>

      <ChartCard title="Review Growth" subtitle="Last 6 months">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={arr(report.reviewGrowth)}>
            <defs><linearGradient id="cr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#291d89" stopOpacity={0.3} /><stop offset="95%" stopColor="#291d89" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip {...chartTooltipStyle()} />
            <Area type="monotone" dataKey="reviews" stroke="#291d89" strokeWidth={2.5} fill="url(#cr)" />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="card-surface p-6">
        <h3 className="font-heading text-base font-semibold text-slate-800">Recommendations for next month</h3>
        <ul className="mt-3 space-y-2">{arr(report.recommendations).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600">• {r}</li>)}</ul>
      </div>
    </div>
  );
}
